import { Field, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames/bind';
import React from 'react';

import { type ComponentProps } from 'lib/component-props';
import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';

import styles from './DynamicButtons.module.scss';
import DynamicButtonSection, { type DynamicButtonSectionProps } from './DynamicButtonSection/DynamicButtonSection';

const cx = classNames.bind(styles);

export interface contextType {
  backBtnLabel: Field<string>;
  currentSection: string;
  setButtonClicked: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentSection: React.Dispatch<React.SetStateAction<string>>;
}

export const DynamicButtonContext = React.createContext<contextType | null>(null);

type DynamicButtonsFields = {
  backLabel: Field<string>;
  initialTitle: Field<string>;
  initialSection: string;
  sections: DynamicButtonSectionProps[];
};

type DynamicButtonsProps = ComponentProps & {
  fields: DynamicButtonsFields;
};

const DynamicButtons = (props: DynamicButtonsProps): JSX.Element => {
  const { className, fields } = props;
  const { backLabel, sections } = fields;

  const [buttonClicked, setButtonClicked] = React.useState<boolean>(false);
  const [currentSection, setCurrentSection] = React.useState(fields.initialSection);
  const headingRefs = React.useRef<Array<HTMLHeadingElement>>([]);

  React.useEffect(() => {
    if (buttonClicked && currentSection && headingRefs && headingRefs?.current) {
      const id = currentSection;
      const heading = headingRefs?.current?.find((element) => element.id === `dynamic-buttons__title--${id}`) || null;

      if (heading) {
        heading.focus();
      }
    }
  }, [buttonClicked, currentSection]);

  const addToHeadingRefs = (header: HTMLHeadingElement | null, index: number): void => {
    if (header && !headingRefs?.current?.includes(header)) {
      headingRefs?.current?.splice(index, 0, header);
    }
  };

  return (
    <DynamicButtonContext.Provider
      value={{
        backBtnLabel: backLabel,
        currentSection,
        setButtonClicked,
        setCurrentSection,
      }}
    >
      <div className={cx('dynamic-buttons', 'component-container-edge', className)}>
        <div className={cx('row', 'justify-content-center', 'mx-0')}>
          <div className={cx('col-10', 'col-md-8')}>
            <div className={cx('dynamic-buttons__wrapper')}>
              {sections?.map((section: DynamicButtonSectionProps, index) => (
                <DynamicButtonSection ref={(header) => addToHeadingRefs(header, index)} key={index} {...section} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </DynamicButtonContext.Provider>
  );
};

export default compose<DynamicButtonsProps>(withDatasourceCheck(), withStyles())(DynamicButtons);
